import { useEffect, useState } from 'react';
import Logger from '@medflex/utils/Logger';
import { trackEvent } from '@medflex/utils/Tracking';

const STORE_KEY = 'notifications';
const isExpired = date => date && new Date(date) <= new Date();
const isSupported = 'Notification' in window;
const write = data => localStorage.setItem(STORE_KEY, JSON.stringify(data));
const read = () => JSON.parse(localStorage.getItem(STORE_KEY));

const useNotifications = () => {
  const [isInitialRequest, setIsInitialRequest] = useState(true);
  const [isGranted, setIsGranted] = useState(false);
  const [isBlocked, setIsBlocked] = useState(false);

  const update = () => {
    const settings = read();
    if (settings) {
      setIsInitialRequest(false);
      setIsGranted(settings.isGranted);
      setIsBlocked(!settings.isGranted && !isExpired(settings.expiryDate));
    }
  };

  useEffect(update, []);

  const requestPermission = async () => {
    try {
      const permission = await Notification.requestPermission();
      trackEvent({
        zone: 'Notification_Request_Permission',
        action: 'permission_granted',
      });
      write({ isGranted: permission === 'granted' });
    } catch (e) {
      Logger.error(`[useNotifications::requestPermission] -> Unexpected error. ${e.message}`);
    }
  };

  useEffect(() => {
    if (isSupported && isGranted) {
      requestPermission();
    }
  }, [isGranted]);

  const deny = () => {
    write({ isGranted: false });
    update();
  };

  const accept = () => {
    write({ isGranted: true });
    update();
  };

  const snooze = (days = 30) => {
    const later = new Date();
    later.setDate(later.getDate() + days);
    write({ isGranted: false, expiryDate: later });
    update();
  };

  const notify = (title, options = {}) => {
    if (isSupported) {
      if (isGranted && Notification.permission === 'granted') {
        return new Notification(title, options);
      }
      Logger.warn(
        '[useNotifications::notify] -> Trying to call notify without permission. ',
        title,
      );
    } else {
      Logger.warn('[useNotifications::notify] -> Notification API not supported. ', title);
    }
    return null;
  };

  return {
    isInitialRequest,
    isBlocked,
    isGranted,
    deny,
    accept,
    snooze,
    notify,
    isSupported,
  };
};

export default useNotifications;
